import { nodeHasChildren } from '@ui/lib/reactFlow/helpers'
import { deleteNode } from '@ui/lib/reactFlow/nodeMethods/deleteNode'
import { useEffect, useMemo, useState } from 'react'
import { NodeProps } from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import { Source } from '@ui/__generated__/graphql'
import { NodeTypeEnum } from '@ui/interfaces'
import { GetSourcesLazyQuery } from '@ui/loaders/sources'
import { NodeOptionButtons } from '@ui/pages/pipelinesWorkflow/components/NodeTypes/NodeOptions'
import { useAuth } from '@ui/providers/AuthProvider.tsx'
import { useNodeAndEdgeStore } from '@ui/stores'

import { NodeContent } from './NodeContent'

interface NodeContainerProps {
  nodeIsSelected: boolean
  nodeType: NodeTypeEnum
  node: NodeProps
  onClick: () => void
}

export const NodeContainerV2 = ({ nodeIsSelected, nodeType, node, onClick }: NodeContainerProps) => {
  const { isDemoMode } = useAuth()

  const [getSources] = GetSourcesLazyQuery()

  const { nodes, edges } = useNodeAndEdgeStore(
    useShallow((state) => ({
      nodes: state.nodes,
      edges: state.edges,
    }))
  )

  const [sources, setSources] = useState<Source[]>([])
  const [showMenu, setShowMenu] = useState(false)

  // close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showMenu && e.target instanceof HTMLElement && !e.target.closest('#menu-container')) {
        setShowMenu(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [showMenu])

  const hasChildren = useMemo(() => {
    return nodeHasChildren(edges, node.id)
  }, [edges, node.id])

  useEffect(() => {
    if (nodeType !== NodeTypeEnum.FIREHOSE) return
    getSources({
      onCompleted: (data) => setSources(data.sources),
    })
  }, [getSources, nodeType])

  const dataTestId = {
    [NodeTypeEnum.FIREHOSE]: `firehose-node`,
    [NodeTypeEnum.CODE]: `code-node-${node.id}`,
    [NodeTypeEnum.EXPORT]: `export-node-${node.id}`,
    [NodeTypeEnum.PLACEHOLDER]: `placeholder-node-${node.id}`,
    [NodeTypeEnum.TRANSFORM]: `transformation-node-${node.id}`,
  }[nodeType]
  const title = {
    [NodeTypeEnum.FIREHOSE]: 'View telemetry data',
    [NodeTypeEnum.CODE]: 'View code',
    [NodeTypeEnum.EXPORT]: 'View export options',
    [NodeTypeEnum.PLACEHOLDER]: 'Add transformation or destination',
    [NodeTypeEnum.TRANSFORM]: 'View transformation options',
  }[nodeType]
  const hideNodeOptions =
    node.data.isMinimap || (!hasChildren && nodeType !== NodeTypeEnum.EXPORT) || nodeType === NodeTypeEnum.FIREHOSE

  return (
    <div className="group relative" data-testid={dataTestId}>
      <div onClick={onClick} className="flex w-60 cursor-pointer flex-col space-y-2 rounded-md" title={title}>
        <div className="flex flex-row items-start justify-between">
          <div className="flex-1">
            {
              <NodeContent
                node={node}
                nodeIsSelected={nodeIsSelected}
                deleteNode={deleteNode}
                isDemoMode={isDemoMode}
                hasChildren={hasChildren}
                sources={sources}
              />
            }
          </div>
        </div>
        {(!hideNodeOptions || (nodeType === NodeTypeEnum.FIREHOSE && !node.data.isMinimap)) && (
          <NodeOptionButtons edges={edges} nodes={nodes} node={node} handleClick={onClick} />
        )}
      </div>
    </div>
  )
}
